import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Pencil, X } from 'lucide-react';
import { getDashboardThemeClasses } from './dashboard/dashboardTheme.ts';

interface AdCampaignNameEditDialogProps {
  isOpen: boolean;
  isDark: boolean;
  sourceId: string;
  initialAlias: string | null;
  isSaving: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: (alias: string | null) => void;
}

const MAX_ALIAS_LENGTH = 120;

const normalizeAlias = (value: string | null | undefined): string | null => {
  const normalized = value?.trim() || '';
  return normalized ? normalized : null;
};

const AdCampaignNameEditDialog: React.FC<AdCampaignNameEditDialogProps> = ({
  isOpen,
  isDark,
  sourceId,
  initialAlias,
  isSaving,
  errorMessage,
  onClose,
  onSave,
}) => {
  const theme = getDashboardThemeClasses(isDark);
  const [alias, setAlias] = useState(initialAlias || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setAlias(initialAlias || '');
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(focusTimer);
      previousFocusRef.current?.focus();
    };
  }, [initialAlias, isOpen]);

  const normalizedAlias = useMemo(() => normalizeAlias(alias), [alias]);
  const initialNormalizedAlias = useMemo(() => normalizeAlias(initialAlias), [initialAlias]);
  const hasChanged = normalizedAlias !== initialNormalizedAlias;

  if (!isOpen) return null;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isSaving && hasChanged) {
      onSave(normalizedAlias);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !isSaving) {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }

    if (event.key === 'Tab' && dialogRef.current) {
      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        aria-label="Fechar edição do nome do anúncio"
        tabIndex={-1}
        className="fixed inset-0 cursor-default bg-black/50 backdrop-blur-sm"
        onClick={() => !isSaving && onClose()}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ad-campaign-name-dialog-title"
        aria-describedby="ad-campaign-name-dialog-description"
        className={`relative z-[10000] w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl ${theme.surface}`}
      >
        <header className={`flex items-start justify-between gap-4 border-b px-5 py-4 ${theme.border}`}>
          <div className="flex min-w-0 items-start gap-3">
            <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl ${theme.accentSurface}`}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 id="ad-campaign-name-dialog-title" className={`text-base font-semibold ${theme.primaryText}`}>
                Nome do anúncio
              </h2>
              <p id="ad-campaign-name-dialog-description" className={`mt-1 text-xs leading-5 ${theme.mutedText}`}>
                Use um nome fácil de reconhecer nos relatórios. O ID original permanece inalterado.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Fechar"
            className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/45 hover:text-[#F8FAFC]' : 'text-sminy-muted hover:bg-slate-100 hover:text-sminy-text-primary'}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-5 py-5">
            <div>
              <label htmlFor="ad-campaign-alias" className={`mb-2 block text-sm font-medium ${theme.primaryText}`}>
                Nome personalizado
              </label>
              <input
                ref={inputRef}
                id="ad-campaign-alias"
                type="text"
                value={alias}
                maxLength={MAX_ALIAS_LENGTH}
                disabled={isSaving}
                onChange={(event) => setAlias(event.target.value)}
                placeholder="Ex.: Implante — vídeo depoimento"
                className={`h-11 w-full rounded-xl border px-3 text-sm outline-none transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 disabled:cursor-not-allowed disabled:opacity-60 ${isDark ? 'placeholder:text-[#7E8CA0]' : 'placeholder:text-slate-400'} ${theme.input}`}
              />
              <div className={`mt-1.5 flex items-center justify-between gap-3 text-xs ${theme.mutedText}`}>
                <span>Deixe vazio para remover somente o nome personalizado.</span>
                <span aria-label={`${alias.length} de ${MAX_ALIAS_LENGTH} caracteres`}>
                  {alias.length}/{MAX_ALIAS_LENGTH}
                </span>
              </div>
            </div>

            <div className={`rounded-xl border px-3 py-2.5 ${isDark ? 'border-[#27364D] bg-[#071225]/45' : 'border-sminy-border bg-slate-50'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${theme.mutedText}`}>ID do anúncio</p>
              <p className={`mt-1 break-all font-mono text-xs ${theme.secondaryText}`}>{sourceId}</p>
            </div>

            {errorMessage && (
              <p role="alert" className={`rounded-xl border px-3 py-2.5 text-sm ${isDark ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
                {errorMessage}
              </p>
            )}
          </div>

          <footer className={`flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:justify-end ${theme.border}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${theme.control}`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || !hasChanged}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Salvar nome
            </button>
          </footer>
        </form>
      </section>
    </div>,
    document.body,
  );
};

export default AdCampaignNameEditDialog;
